import { create } from "zustand";
import { persist } from "zustand/middleware";

function systemPrefersDark() {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function resolve(theme) {
  return theme === "system" ? (systemPrefersDark() ? "dark" : "light") : theme;
}

// Also flips the meta[name=theme-color] the OS/browser chrome uses (e.g.
// the address bar tint) — the two static media-query tags in index.html
// only handle "system", this covers an explicit manual override too.
function applyTheme(theme) {
  const isDark = resolve(theme) === "dark";
  document.documentElement.classList.toggle("dark", isDark);
  document.querySelectorAll('meta[name="theme-color"]').forEach((tag) => tag.remove());
  const meta = document.createElement("meta");
  meta.name = "theme-color";
  meta.content = isDark ? "#0a0a0b" : "#faf9f7";
  document.head.appendChild(meta);
}

// theme: "light" | "dark" | "system". The public/index.html boot script
// applies the persisted value before first paint (reading the same
// "sc-theme" localStorage key) so there's no flash of the wrong theme.
const useThemeStore = create(
  persist(
    (set, get) => ({
      theme: "system",
      resolvedTheme: resolve("system"),

      setTheme: (theme) => {
        set({ theme, resolvedTheme: resolve(theme) });
        applyTheme(theme);
      },

      toggle: () => {
        const next = resolve(get().theme) === "dark" ? "light" : "dark";
        set({ theme: next, resolvedTheme: next });
        applyTheme(next);
      },
    }),
    {
      name: "sc-theme",
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.resolvedTheme = resolve(state.theme);
        applyTheme(state.theme);
      },
    }
  )
);

// Live-follow the OS setting while the user hasn't picked an explicit
// theme, instead of only re-checking it on the next full page load.
if (window.matchMedia) {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (useThemeStore.getState().theme === "system") {
      applyTheme("system");
      useThemeStore.setState({ resolvedTheme: resolve("system") });
    }
  });
}

export default useThemeStore;
